import { 
  Palette, 
  Scissors, 
  Sparkles, 
  Calendar, 
  Users, 
  Camera,
  Shield,
  Zap,
  BarChart3,
  Clock,
  TrendingUp,
  Globe,
  Lock,
  Settings,
  Star,
  Target,
  Activity,
  Rocket,
  Eye,
  Database,
  Network,
  CheckCircle,
  Heart,
  Package,
  Brain,
  Smartphone
} from 'lucide-react';
import { ProductConfigs } from './types';

export const beautyFashionSolutionsConfigs: ProductConfigs = {
  'fashion-design-studio': {
    keyBenefits: [
      {
        icon: Palette,
        title: 'Digital Design Studio',
        description: 'Advanced fashion design tools with 3D visualisation, fabric simulation, and collaborative design workflows for creative excellence.',
        metrics: '70% faster design iteration cycles',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Users,
        title: 'Supply Chain Integration',
        description: 'End-to-end supply chain management with supplier coordination, material sourcing, and production timeline tracking.',
        metrics: '50% reduction in production delays',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Trend Analysis & Forecasting',
        description: 'AI-powered trend prediction with market analysis, consumer behaviour insights, and seasonal forecasting for strategic planning.',
        metrics: '85% accuracy in trend prediction',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Sparkles,
        title: 'Creative Asset Management',
        description: 'Comprehensive digital asset library with version control, collaborative editing, and intelligent tagging for creative resources.',
        metrics: '80% faster asset retrieval',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Eye,
        title: 'Quality Assurance System',
        description: 'Automated quality control with pattern validation, measurement accuracy, and design consistency checks across collections.',
        metrics: '95% reduction in production errors',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Star,
        title: 'Brand Consistency Engine',
        description: 'Maintains brand identity across all designs with automated brand guideline enforcement and style coherence validation.',
        metrics: '90% brand consistency improvement',
        color: 'from-violet-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Enhanced Design Tools',
        description: 'Machine learning-powered design assistance with pattern generation, colour palette suggestions, and automated design optimisation.',
        metrics: '60% improvement in design productivity',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Global Market Intelligence',
        description: 'Real-time global fashion insights with regional trend analysis, competitive intelligence, and market opportunity identification.',
        metrics: 'Access to 200+ global fashion markets',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Camera,
        title: 'Virtual Prototyping System',
        description: 'Advanced 3D prototyping with virtual fitting, photorealistic rendering, and augmented reality preview capabilities.',
        metrics: '80% reduction in physical prototypes',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Settings,
        title: 'Automated Production Planning',
        description: 'Intelligent production scheduling with resource optimization, timeline prediction, and automated workflow coordination.',
        metrics: '65% improvement in production efficiency',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Predictive Size Grading',
        description: 'AI-powered size grading with fit prediction, regional sizing adjustments, and automated pattern scaling for global markets.',
        metrics: '90% fit accuracy across all sizes',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Activity,
        title: 'Real-Time Collaboration Hub',
        description: 'Advanced collaboration platform with real-time design sharing, version control, and cross-team communication tools.',
        metrics: '75% faster design approval cycles',
        color: 'from-teal-500 to-green-600'
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Shield,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'beauty-salon-management': {
    keyBenefits: [
      {
        icon: Scissors,
        title: 'Complete Salon Operations',
        description: 'Comprehensive salon management with appointment scheduling, staff coordination, inventory tracking, and customer relationship management.',
        metrics: '90% improvement in booking efficiency',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Calendar,
        title: 'Smart Appointment System',
        description: 'AI-powered scheduling with automated reminders, waitlist management, and optimal staff allocation for maximum productivity.',
        metrics: '40% reduction in no-shows',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Customer Experience Management',
        description: 'Personalised customer profiles with service history, preferences tracking, and automated follow-up campaigns for retention.',
        metrics: '65% increase in customer retention',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Heart,
        title: 'Wellness Integration Platform',
        description: 'Holistic beauty and wellness service coordination with treatment tracking, health monitoring, and lifestyle recommendations.',
        metrics: '85% improvement in customer satisfaction',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Package,
        title: 'Retail Product Management',
        description: 'Integrated product sales with inventory automation, recommendation engine, and loyalty program coordination.',
        metrics: '150% increase in retail revenue',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Performance Analytics Suite',
        description: 'Comprehensive business intelligence with staff performance, revenue optimization, and predictive growth analytics.',
        metrics: '200% improvement in business insights',
        color: 'from-violet-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Recommendations',
        description: 'Machine learning algorithms analyse customer data to suggest personalised treatments, products, and service packages.',
        metrics: '45% increase in service upsells',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Business Intelligence Dashboard',
        description: 'Comprehensive analytics with revenue tracking, staff performance metrics, and predictive business insights.',
        metrics: '35% improvement in profitability',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Star,
        title: 'Loyalty & Rewards System',
        description: 'Automated loyalty programme with points tracking, reward redemption, and personalised promotional campaigns.',
        metrics: '80% increase in repeat bookings',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Brain,
        title: 'Intelligent Resource Optimization',
        description: 'AI-driven staff scheduling, equipment utilization, and service flow optimization for maximum operational efficiency.',
        metrics: '70% improvement in resource utilization',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Management',
        description: 'Centralized management for multiple salon locations with unified reporting, staff coordination, and brand consistency.',
        metrics: 'Manage unlimited locations seamlessly',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Customer Experience',
        description: 'Comprehensive mobile app for customers with booking, payments, loyalty tracking, and beauty consultation features.',
        metrics: '4.8/5 customer app satisfaction',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Shield,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'cosmetics-brand-platform': {
    keyBenefits: [
      {
        icon: Sparkles,
        title: 'Brand Management Suite',
        description: 'Comprehensive brand management with product development tracking, campaign coordination, and multi-channel brand consistency.',
        metrics: '60% improvement in brand consistency',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Globe,
        title: 'Global Market Expansion',
        description: 'International market penetration tools with regulatory compliance, localisation management, and distribution coordination.',
        metrics: 'Launch in 50+ markets simultaneously',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Consumer Insights Platform',
        description: 'Advanced consumer behaviour analytics with sentiment analysis, trend tracking, and predictive market intelligence.',
        metrics: '75% accuracy in consumer trend prediction',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Brain,
        title: 'AI Brand Intelligence',
        description: 'Machine learning-powered brand positioning with competitive analysis, market opportunity identification, and strategic recommendations.',
        metrics: '90% improvement in market positioning',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Influencer Collaboration Hub',
        description: 'Comprehensive influencer management with partnership tracking, campaign coordination, and performance analytics.',
        metrics: '300% increase in influencer ROI',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Star,
        title: 'Product Innovation Engine',
        description: 'Data-driven product development with trend analysis, consumer feedback integration, and market validation systems.',
        metrics: '80% faster product innovation cycles',
        color: 'from-violet-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Driven Product Development',
        description: 'Machine learning algorithms analyse market trends, consumer preferences, and ingredient efficacy for optimal product formulation.',
        metrics: '50% faster product development cycles',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Camera,
        title: 'Virtual Try-On Technology',
        description: 'Advanced AR/VR integration with virtual makeup application, skin tone matching, and photorealistic product visualization.',
        metrics: '200% increase in online engagement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Settings,
        title: 'Omnichannel Integration',
        description: 'Seamless integration across retail, e-commerce, social media, and mobile platforms with unified inventory and customer data.',
        metrics: '90% cross-channel consistency',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Eye,
        title: 'Consumer Behavior Analytics',
        description: 'Advanced consumer behavior tracking with purchase patterns, engagement metrics, and predictive modeling for strategic planning.',
        metrics: '95% accuracy in consumer prediction',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Precision Marketing Engine',
        description: 'AI-powered marketing optimization with audience segmentation, campaign personalization, and conversion optimization.',
        metrics: '250% improvement in marketing ROI',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Activity,
        title: 'Real-Time Market Intelligence',
        description: 'Continuous market monitoring with competitor analysis, trend detection, and strategic opportunity identification.',
        metrics: 'Monitor 10,000+ market signals daily',
        color: 'from-emerald-500 to-teal-600'
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Shield,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'fashion-retail-platform': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Omnichannel Retail Experience',
        description: 'Unified retail platform with seamless online-offline integration, inventory synchronisation, and consistent customer experience.',
        metrics: '85% improvement in customer satisfaction',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: BarChart3,
        title: 'Inventory & Demand Planning',
        description: 'AI-powered inventory optimisation with demand forecasting, automated reordering, and seasonal trend analysis.',
        metrics: '70% reduction in inventory costs',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Personalised Shopping Experience',
        description: 'Machine learning-driven personalisation with style recommendations, size predictions, and curated product suggestions.',
        metrics: '120% increase in conversion rates',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Brain,
        title: 'AI-Powered Fashion Intelligence',
        description: 'Advanced AI analytics for fashion trend prediction, consumer behavior analysis, and strategic merchandise planning.',
        metrics: '95% accuracy in trend forecasting',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Heart,
        title: 'Customer Loyalty Engine',
        description: 'Comprehensive loyalty program with personalized rewards, VIP experiences, and retention optimization strategies.',
        metrics: '180% increase in customer lifetime value',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: Package,
        title: 'Supply Chain Optimization',
        description: 'End-to-end supply chain management with vendor coordination, quality control, and sustainable sourcing initiatives.',
        metrics: '60% improvement in supply chain efficiency',
        color: 'from-amber-500 to-orange-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'Smart Visual Search',
        description: 'Advanced image recognition with style matching, colour coordination, and trend-based product discovery capabilities.',
        metrics: '300% improvement in product discovery',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Global Fashion Intelligence',
        description: 'Real-time fashion trend analysis with global market insights, influencer tracking, and seasonal forecasting.',
        metrics: 'Track 1000+ global fashion influencers',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Star,
        title: 'Social Commerce Integration',
        description: 'Seamless social media integration with shoppable posts, influencer partnerships, and viral marketing campaign management.',
        metrics: '400% increase in social-driven sales',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Camera,
        title: 'AR Virtual Fitting Room',
        description: 'Advanced augmented reality technology for virtual try-ons, size fitting, and style visualization before purchase.',
        metrics: '85% reduction in return rates',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Target,
        title: 'Dynamic Pricing Engine',
        description: 'AI-powered pricing optimization with market analysis, competitor monitoring, and demand-based pricing strategies.',
        metrics: '45% improvement in profit margins',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Activity,
        title: 'Real-Time Analytics Platform',
        description: 'Comprehensive business intelligence with sales performance, customer insights, and predictive analytics for strategic decisions.',
        metrics: 'Process 1M+ data points per second',
        color: 'from-emerald-500 to-teal-600'
      }
    ],
    securityPerformance: [
      {
        icon: Target,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Shield,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  }
};